/**
 * @fileoverview Main App component orchestrating providers and routing
 *
 * This component serves as the root of the application component tree,
 * setting up essential providers and the routing system.
 *
 * Architecture:
 * - Wraps the entire app in AuthProvider for authentication context
 * - Includes AppRouter for client-side routing with route protection
 * - Maintains clean separation between auth and routing concerns
 * - Follows the provider pattern for dependency injection
 *
 * The component hierarchy flows:
 * App → AuthProvider → AppRouter → Route Guards → Page Components
 *
 * This setup ensures authentication context is available throughout
 * the application and route protection works correctly.
 */

import './app.css';

import { AuthProvider } from '@/providers';
import { AppRouter } from '@/router/app-router';

/**
 * Root application component
 *
 * Sets up the application's core infrastructure:
 * 1. Authentication provider (AuthProvider) - provides auth context to all components
 * 2. Routing system (AppRouter) - handles client-side navigation and route protection
 *
 * The AuthProvider must wrap the router to ensure authentication state
 * is available to route guards (ProtectedRoute and PublicRoute).
 *
 * @returns The complete application with providers and routing
 *
 * @example
 * ```tsx
 * // Usage in main.tsx
 * createRoot(document.getElementById('root')!).render(
 *   <StrictMode>
 *     <App />
 *   </StrictMode>,
 * )
 * ```
 */
function App() {
  return (
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  );
}

export default App;
